import React, { useState, useEffect, useMemo } from 'react';
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button,
	IconButton,
	Box,
	CircularProgress,
	Typography,
	useMediaQuery,
	useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CommentEditor from 'containers/pages/comments/components/editor/CommentEditor';
import useCommentEditor from 'containers/pages/comments/hooks/useCommentEditor';
import { CANtrackComment, CANtrackCase } from 'containers/pages/comments/types/comment';
import { convertToCANtrackCase } from 'containers/pages/comments/utils/convertToCANtrackCase';
import getCase from 'api/comments/getCase';
import ConfirmationDialog from 'components/confirmationDialog/ConfirmationDialog';

interface CANtrackCommentDialogProps {
	open: boolean;
	caseId: number;
	caseTitle: string;
	taskTitle: string;
	onClose: () => void;
	onSaved?: () => void;
}

const CANtrackCommentDialog: React.FC<CANtrackCommentDialogProps> = ({
	open,
	caseId,
	caseTitle,
	taskTitle,
	onClose,
	onSaved,
}) => {
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
	const [fullCase, setFullCase] = useState<CANtrackCase | null>(null);
	const [loadingCase, setLoadingCase] = useState(true);
	const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);

	// Local no-op state for setComments — we don't maintain a comment list in TODO
	const [, setComments] = useState<CANtrackComment[]>([]);

	// Build an initial comment object to pre-fill the form — memoized to avoid
	// infinite re-render loops (useCommentEditor resets form on initialComment change,
	// which triggers ActivityGPDA's fetchActivities, causing re-renders)
	const initialComment: CANtrackComment | undefined = useMemo(() => {
		if (!fullCase) return undefined;
		return {
			id: 0, // 0 = new comment
			title: taskTitle,
			description: '',
			date: new Date(),
			timeRegular: 0.1,
			timeOver: 0,
			caseID: fullCase.id,
			caseTitle: fullCase.title,
			customerID: fullCase.customerID,
			customerName: fullCase.customerName,
			gpda: {
				group: fullCase.gpd.group,
				project: fullCase.gpd.project,
				deliverable: fullCase.gpd.deliverable,
				activity: { id: 0, title: '' }, // Will be selected by user
			},
			billable: true,
		};
	}, [fullCase, taskTitle]);

	const {
		formState,
		errors,
		handleChange,
		handleSaveComment,
		handleResetComment,
	} = useCommentEditor(setComments, initialComment);

	// Fetch full case details on open
	useEffect(() => {
		if (!open) {
			setFullCase(null);
			setLoadingCase(true);
			return;
		}

		let cancelled = false;

		async function fetchFullCase() {
			setLoadingCase(true);
			try {
				const raw = await getCase(caseId);
				if (!cancelled) {
					setFullCase(convertToCANtrackCase(raw));
				}
			} catch (error) {
				console.error('[CANtrackCommentDialog] Failed to fetch case details:', error);
			} finally {
				if (!cancelled) setLoadingCase(false);
			}
		}

		fetchFullCase();
		return () => {
			cancelled = true;
		};
	}, [open, caseId]);

	const hasUnsavedChanges = (): boolean => {
		if (!fullCase) return false;
		return (
			formState.description.trim() !== '' ||
			formState.selectedActivity !== null ||
			formState.time.regularTime !== 0.1 ||
			formState.time.overTime !== 0
		);
	};

	const handleClose = () => {
		if (hasUnsavedChanges()) {
			setDiscardConfirmOpen(true);
		} else {
			onClose();
		}
	};

	const handleSave = async () => {
		const success = await handleSaveComment();
		if (!success) return;
		onSaved?.();
		onClose();
	};

	return (
		<>
			<Dialog
				open={open}
				onClose={handleClose}
				maxWidth="md"
				fullWidth
				fullScreen={isMobile}
			>
				<DialogTitle
					sx={{
						display: 'flex',
						justifyContent: 'space-between',
						alignItems: 'center',
					}}
				>
					Log CANtrack Comment
					<IconButton onClick={handleClose} edge="end">
						<CloseIcon />
					</IconButton>
				</DialogTitle>
				<DialogContent>
					{loadingCase ? (
						<Box
							sx={{
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'center',
								gap: 1,
								py: 4,
							}}
						>
							<CircularProgress size={24} />
							<Typography>Loading case details...</Typography>
						</Box>
					) : fullCase ? (
						<Box sx={{ pt: 1 }}>
							<CommentEditor
								comment={initialComment}
								formState={formState}
								errors={errors}
								handleChange={handleChange}
								handleSave={handleSave}
								handleReset={handleResetComment}
								handleDelete={() => {}}
								hideFilters
								disableCaseSelection
								linearLayout
								hideButtons
								resizableComment
							/>
						</Box>
					) : (
						<Typography color="error" sx={{ py: 2 }}>
							Failed to load case details. Please try again.
						</Typography>
					)}
				</DialogContent>
				<DialogActions>
					<Button onClick={handleClose}>
						Back
					</Button>
					<Button onClick={handleResetComment}>
						Reset
					</Button>
					<Button
						variant="contained"
						color="primary"
						onClick={handleSave}
						disabled={loadingCase || !fullCase}
					>
						Save
					</Button>
				</DialogActions>
			</Dialog>

			<ConfirmationDialog
				open={discardConfirmOpen}
				onClose={() => setDiscardConfirmOpen(false)}
				onConfirm={() => {
					setDiscardConfirmOpen(false);
					onClose();
				}}
				title="Discard unsaved changes?"
				description="You have unsaved changes to this comment. Are you sure you want to close without saving?"
				confirmText="Discard"
				cancelText="Keep Editing"
			/>
		</>
	);
};

export default CANtrackCommentDialog;
